import httpClient from './httpClient';
import { AxiosResponse } from 'axios';
import { User } from '../types/User';
import { LoginResponseModel } from './user';
import { OrganizationAccessRoles } from '../common/auth';
import { WorkspaceAccessRole } from '../types/WorkspaceAccess';
import { Notification } from '../types/Notification';

const ORGANIZATION_BASE_ROUTE = '/v1/organization';

export enum USER_STATE_IN_ORGANIZATION {
  INVITED = 'INVITED',
  ONBOARDED = 'ONBOARDED',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DELETED = 'DELETED',
}

enum USER_INVITATION_REQUEST_STATUS {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
}

export type OrganizationAccess = {
  id: number;
  organizationId: number;
  userId: number;
  roleToAssume: string;
  createdAt: Date;
  updatedAt: Date;
  usersStateInOrganization: USER_STATE_IN_ORGANIZATION;
};

export type UserInvitation = {
  id: number;
  email: string;
  invitedAsRole: string;
  invitedBy: number;
  comment: string;
  status: USER_INVITATION_REQUEST_STATUS;
  organizationId: number;
  createdAt: Date;
  updatedAt: Date;
  Workspaces: UserInvitationOnWorkspace[];
};

export type UserInvitationOnWorkspace = {
  userInvitationId: number;
  workspaceId: number;
  role: string;
};

export type Organization = {
  id: number;
  publicId: string;
  name: string;
  state: string;
  deletedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  // logoUrl?: string;
  organizationPicturePath?: string | null;
  organizationPicture?: number | null;
  // Self-sort firmographics (owner-only). Populated from the live GET /organization.
  companySize?: string | null;
  referralSource?: string | null;
  previousTool?: string | null;
};

// Expected responses
export interface CreateOrganizationResponse {
  createdOrganization: UserOrganizationAccess;
  accessGranted: OrganizationAccess;
  organizationsAccess: UserOrganizationAccess[];
}

export interface UserOrganizationAccessAndInvites {
  organizationsAccess: UserOrganizationAccess[];
  organizationsInvites: UserOrganizationInvite[];
  organizationsToJoin: UserOrganizationAccess[];
}

export interface UserOrganizationAccess extends Organization {
  OrganizationAccess: OrganizationAccess[];
}

export interface UserOrganizationInvite extends UserOrganizationAccess {
  UserInvitation: UserInvitation[];
}

export interface OrganizationUsersResponse extends OrganizationAccess {
  User: User;
}

export async function createOrganization(name: string): Promise<AxiosResponse<CreateOrganizationResponse>> {
  return httpClient({
    method: 'POST',
    url: `${ORGANIZATION_BASE_ROUTE}/`,
    data: {
      name,
    },
  });
}

export async function getUserOrganizationAccess(): Promise<AxiosResponse<UserOrganizationAccess[]>> {
  return httpClient({
    method: 'GET',
    url: `${ORGANIZATION_BASE_ROUTE}/access`,
  });
}

export async function getUserOrganizationAccessAndInvites(): Promise<
  AxiosResponse<UserOrganizationAccessAndInvites[]>
> {
  return httpClient({
    method: 'GET',
    url: `${ORGANIZATION_BASE_ROUTE}/access-and-invites`,
  });
}

export async function getOrganizationUsers(
  organizationId: number
): Promise<AxiosResponse<OrganizationUsersResponse[]>> {
  return httpClient({
    method: 'GET',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/users`,
  });
}

export interface OrganizationDeleteResponse {
  organization: Organization;
  deletionProcessStatus: string;
}

export async function triggerOrganizationDeletion(
  organizationId: number
): Promise<AxiosResponse<OrganizationDeleteResponse>> {
  return httpClient({
    method: 'DELETE',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}`,
  });
}

export interface OrganizationUpdateResponse {
  organizationWithAccess: UserOrganizationAccess;
}

export async function updateOrganization(
  orgId: number,
  updatedFields: Partial<Organization>
): Promise<AxiosResponse<OrganizationUpdateResponse>> {
  return httpClient({
    method: 'PUT',
    url: `${ORGANIZATION_BASE_ROUTE}/${orgId}`,
    data: {
      ...updatedFields,
    },
  });
}

export async function updateOrganizationMetadata(
  orgId: number,
  name: string,
  organizationPicture?: { id: number | null; path: string | null }
): Promise<AxiosResponse<OrganizationUpdateResponse>> {
  return httpClient({
    method: 'PUT',
    url: `${ORGANIZATION_BASE_ROUTE}/${orgId}`,
    data: {
      name,
      ...(organizationPicture && {
        organizationPicture: organizationPicture.id,
        organizationPicturePath: organizationPicture.path,
      }),
    },
  });
}

export async function getUserInvitationRequests(organizationId: number): Promise<AxiosResponse<UserInvitation[]>> {
  return httpClient({
    method: 'GET',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/invited`,
  });
}

export async function verifyUserInvitation(
  organizationId: number,
  invitationId: number
): Promise<AxiosResponse<LoginResponseModel>> {
  return await httpClient({
    method: 'POST',
    url: `${ORGANIZATION_BASE_ROUTE}/verify-invitation`,
    data: {
      organizationId,
      invitationId,
    },
  });
}

export async function updateUserRole(
  userId: number,
  organizationId: number,
  role: OrganizationAccessRoles
): Promise<AxiosResponse<OrganizationAccess>> {
  return await httpClient({
    method: 'PUT',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/access/user/${userId}`,
    data: {
      role,
    },
  });
}

export async function deactivateOrgUser(
  userId: number,
  organizationId: number
): Promise<AxiosResponse<OrganizationAccess>> {
  return await httpClient({
    method: 'PUT',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/deactivate/${userId}`,
  });
}

export async function activateOrgUser(
  userId: number,
  organizationId: number
): Promise<AxiosResponse<OrganizationAccess>> {
  return await httpClient.get(`${ORGANIZATION_BASE_ROUTE}/${organizationId}/activate/${userId}`);
}

export async function removeInvitation(invitationId: number, organizationId: number) {
  return await httpClient.get(`${ORGANIZATION_BASE_ROUTE}/${organizationId}/remove-invitation/${invitationId}`);
}

export async function resendInvitation(invitationId: number, organizationId: number) {
  return await httpClient.get(`${ORGANIZATION_BASE_ROUTE}/${organizationId}/resend-invitation/${invitationId}`);
}

export async function updateInvitation(
  invitationId: number,
  organizationId: number,
  role: OrganizationAccessRoles
): Promise<AxiosResponse<UserInvitation>> {
  return await httpClient({
    method: 'PUT',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/invitation/${invitationId}`,
    data: {
      role,
    },
  });
}

export function removeSpaceFromInvitation(
  invitationId: number,
  organizationId: number,
  workspaceId: number
): Promise<AxiosResponse<UserInvitation>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/invitation/${invitationId}/remove-from-space`,
    data: {
      workspaceId,
    },
  });
}

export function updateSpaceRoleInInvitation(
  invitationId: number,
  organizationId: number,
  workspaceId: number,
  role: WorkspaceAccessRole
): Promise<AxiosResponse<UserInvitation>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/invitation/${invitationId}/update-space-role`,
    data: {
      workspaceId,
      role,
    },
  });
}

export async function uploadOrganizationPicture(
  organizationId: number,
  file: File
): Promise<AxiosResponse<OrganizationProfileResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/profile-logo`,
    data: (() => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    })(),
    headers: {
      'Content-Type': 'multipart/form-data',
      enctype: 'multipart/form-data',
    },
  });
}

export interface OrganizationProfileResponse {
  data: {
    id: string;
    path: string;
  };
}

export async function getOrganization(organizationId: number) {
  return await httpClient.get(`${ORGANIZATION_BASE_ROUTE}/${organizationId}`);
}

export type OrgFirmographicsRequest = {
  companySize: string;
  referralSource: string;
  previousTool: string;
};

/** Dedicated self-sort firmographics write (owner/admin-only on the server). */
export async function submitOrgFirmographics(
  orgId: number,
  data: OrgFirmographicsRequest
): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'POST',
    url: `${ORGANIZATION_BASE_ROUTE}/${orgId}/firmographics`,
    data,
  });
}

export async function joinOgranization(organizationId: number): Promise<AxiosResponse<OrganizationAccess>> {
  return await httpClient({
    method: 'POST',
    url: `${ORGANIZATION_BASE_ROUTE}/join`,
    data: {
      organizationId,
    },
  });
}

export async function getUserNotifications(organizationId: number): Promise<AxiosResponse<Notification[]>> {
  return httpClient({
    method: 'GET',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications`,
  });
}

export async function readUserNotification(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}/read`,
  });
}

export async function unreadUserNotification(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}/unread`,
  });
}

export async function markReadAllUnreadUserNotification(organizationId: number): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/read`,
  });
}

export async function markUnreadAllReadUserNotification(organizationId: number): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/unread`,
  });
}

export async function deleteUserNotification(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'DELETE',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}`,
  });
}

export async function deleteReadUserNotifications(organizationId: number): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'DELETE',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/read`,
  });
}

export async function deleteCompletedActions(organizationId: number): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'DELETE',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/actions/completed`,
  });
}

export async function getInboxNotifications(
  organizationId: number,
  params: { tab: 'actions' | 'updates'; filter?: string; cursor?: number; limit?: number; includeArchived?: boolean }
): Promise<AxiosResponse<import('../types/Notification').NotificationsEnvelope>> {
  return httpClient({
    method: 'GET',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications`,
    params,
  });
}

export async function setNotificationDone(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}/done`,
  });
}
export async function setNotificationUndone(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}/undone`,
  });
}
export async function archiveNotification(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}/archive`,
  });
}
export async function unarchiveNotification(
  organizationId: number,
  notificationId: number
): Promise<AxiosResponse<Notification>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/${notificationId}/unarchive`,
  });
}

export async function markAllActionsDone(organizationId: number): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/actions/done-all`,
  });
}

export async function markAllActionsUndone(organizationId: number): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'PATCH',
    url: `${ORGANIZATION_BASE_ROUTE}/${organizationId}/notifications/actions/undone-all`,
  });
}
